import { useNow } from '../../hooks'
import { Icon } from '../../ui/Icon'
import { STALE_MS } from './api'
import { clock } from './format'
import { dataAge, type RelayLive } from './live'

/*
 * Freshness of the console's data (handoff §5, 04-admin-api.md §2.2):
 * - older than 15 s → "CENTER UNREACHABLE — data N s old" when requests to the
 *   center fail, "DATA N S OLD" when it answers with old data (no newer
 *   reports from the nodes);
 * - otherwise, stream lost → "LIVE UPDATES LOST — polling every 5 s";
 * - stream refused by the center's limit (429, decision 51) → "LIVE UPDATES
 *   LIMITED", polling, and when the stream is tried again (Retry-After).
 * R0 has no relay actions to disable; the existing ones (Stop stream, node
 * policy) do not depend on relay data and stay as they are.
 */
export function RelayDataBanner({ live }: { live: RelayLive }) {
  const now = useNow(1000)
  if (!live.available) return null
  const age = dataAge(live, now)
  const stale = age !== null && age > STALE_MS
  const limited = live.sse === 'limited'
  const lost = live.sse === 'lost' || limited
  if (!stale && !lost) return null
  const secs = age === null ? 0 : Math.round(age / 1000)
  const retry = limited && live.retryAt ? Math.max(0, Math.ceil((live.retryAt - now) / 1000)) : 0
  const title = stale ? (live.failing ? `CENTER UNREACHABLE — DATA ${secs} S OLD` : `DATA ${secs} S OLD`) : limited ? 'LIVE UPDATES LIMITED' : 'LIVE UPDATES LOST'
  return (
    <div
      role="status"
      data-testid="relay-banner"
      data-kind={stale ? (live.failing ? 'unreachable' : 'stale') : limited ? 'limited' : 'lost'}
      data-retry={limited ? retry : undefined}
      data-age={secs}
      data-state="warn"
      // the system's banner, still (07 «no looping on anything that is not live signal», AUDIT CN-11 / MO-05):
      // the state icon and the word, a warn edge all round as it stands inside the page, not under the top bar
      className="sx-banner mb-4 flex-wrap rounded-md border border-warn/45 max-md:items-start"
    >
      <Icon name="state-warn" size={16} />
      <b className="font-mono text-[11px] tracking-[.08em] text-warn">{title}</b>
      <span className="txt">
        {stale ? (
          <>
            Routes and nodes as of <span className="tabular">{clock(live.asOf)}</span>.{' '}
            {live.failing ? 'The center is not answering; nodes keep streaming on their own.' : 'The center has no newer reports from the nodes.'}
            {lost && (limited ? ' Live updates limited; polling every 5 s.' : ' Live updates lost; trying every 5 s.')}
          </>
        ) : limited ? (
          <>
            Too many live connections for your account (other tabs or devices): the center refused one more. Polling every 5 s instead; the live stream is tried again in{' '}
            <span className="tabular" data-testid="relay-banner-retry">
              {retry} s
            </span>
            . Data as of <span className="tabular">{clock(live.asOf)}</span>.
          </>
        ) : (
          <>Polling every 5 s instead. Data as of <span className="tabular">{clock(live.asOf)}</span>.</>
        )}
      </span>
    </div>
  )
}
